import React from 'react';
import LibrarySearchInput from '../common/LibrarySearchInput';
import LibraryFilterChips, { type LibraryFilterChip } from '../common/LibraryFilterChips';
import LibraryFilterSheet, { LibraryFilterSheetGroup } from '../common/LibraryFilterSheet';
import LibrarySavedViews from '../common/LibrarySavedViews';
import { LibraryAdvancedToggle } from '../../ui/LibraryAdvancedToggle';
import { LibraryFilterToggle } from '../../ui/LibraryFilterToggle';
import { LibraryViewToggle, type LibraryViewOption } from '../../ui/LibraryViewToggle';
import type { SavedViewsController } from '../../hooks/useSavedViews';
import useLibraryPhoneLayout from '../../hooks/useLibraryPhoneLayout';
import './LibraryList.css';

export type LibraryListSortDir = 'asc' | 'desc';
export type LibraryListFacetOption = { value: string; label: string; count?: number | null };
export type LibraryListQuickFilter = {
  key: string;
  label: string;
  count?: number | null;
  active: boolean;
  /** Amber when it points at something late. */
  tone?: 'warn';
};
/** A labelled filter beyond sites and status (e.g. Inventory's category select). */
export type LibraryListExtraFilter = {
  key: string;
  label: string;
  control: React.ReactNode;
  /** Counts toward the phone filter button's badge. */
  active: boolean;
};
export type LibraryListSortOption = {
  value: string;
  label: string;
  /**
   * The option's text for each direction. Left out: "<label>, first to last" / "<label>, last to
   * first". `null` leaves that direction out of the menu (the server has no such sort).
   */
  ascLabel?: string | null;
  descLabel?: string | null;
};

type LibraryListToolbarProps<C, V extends string> = {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  /** A shorter hint for the compact toolbar (≤720px), whose search box is too narrow for the full one. */
  searchPlaceholderShort?: string;
  searchLabel: string;
  /**
   * The site picker (SearchableMultiSelect), mounted once: inline or in the phone sheet. Leave it
   * out when the page picks the site elsewhere (Inventory's hero site picker).
   */
  siteControl?: React.ReactNode;
  siteFilterActive?: boolean;
  /** A segmented facet (direction, type, kind). Leave the options out for none. */
  facetLabel?: string;
  facetOptions?: ReadonlyArray<LibraryListFacetOption>;
  facet?: string;
  onFacetChange?: (value: string) => void;
  /** The status picker (StageFilterToolbar). Leave it out for a list with no status. */
  statusControl?: React.ReactNode;
  statusFilterActive?: boolean;
  /** Filters after status, each mounted once: inline with its label or in the phone sheet. */
  extraFilters?: ReadonlyArray<LibraryListExtraFilter>;
  quickFilters: ReadonlyArray<LibraryListQuickFilter>;
  onToggleQuickFilter: (key: string) => void;
  /** e.g. a "Linked to 0917102 ×" chip from an event link. */
  extraChip?: { label: string; onClear: () => void } | null;
  countText: React.ReactNode;
  /** List, Grid, Calendar. Leave out (or pass one option) for a list with no view switch. Phones drop Grid,
   * whose cards are List's there. */
  viewOptions?: ReadonlyArray<LibraryViewOption<V>>;
  view?: V;
  onViewChange?: (view: V) => void;
  /** Names the view switch, e.g. "Warehouse view". */
  viewAriaLabel?: string;
  /** Buttons after the count and view switch (Columns, Export CSV). */
  metaActions?: React.ReactNode;
  advancedOpen: boolean;
  onToggleAdvanced: () => void;
  /** How many Advanced fields differ from the defaults. */
  advancedActiveCount: number;
  /** Fields for the Advanced grid (date ranges, vendor, grouping, fold). */
  advancedFields: React.ReactNode;
  sortOptions: ReadonlyArray<LibraryListSortOption>;
  sortKey: string;
  sortDir: LibraryListSortDir;
  onSortChange: (sortKey: string, sortDir: LibraryListSortDir) => void;
  savedViews: SavedViewsController<C>;
  /** Puts back the Advanced fields only. */
  onResetAdvanced: () => void;
  /** Puts back everything the phone sheet holds. */
  onResetAll: () => void;
  /** ≤720px: the filter sheet replaces the inline filters. */
  compact: boolean;
  sheetResultLabel: string;
  /** Told when the phone filter sheet opens or closes (to load a long option list only then). */
  onSheetOpenChange?: (open: boolean) => void;
};

const NO_FACETS: ReadonlyArray<LibraryListFacetOption> = [];
const NO_EXTRA_FILTERS: ReadonlyArray<LibraryListExtraFilter> = [];

/**
 * A server-paged library's toolbar: search, sites, an optional facet, status, quick filters, the
 * count and view switch, and the Advanced panel (fields, sort, saved views). At ≤720px the filters
 * move into the phone sheet. Shared by the Warehouse tabs and the Invoicing library.
 */
export default function LibraryListToolbar<C, V extends string>({
  search,
  onSearchChange,
  searchPlaceholder,
  searchPlaceholderShort,
  searchLabel,
  siteControl,
  siteFilterActive = false,
  facetLabel = '',
  facetOptions = NO_FACETS,
  facet = '',
  onFacetChange,
  statusControl,
  statusFilterActive = false,
  extraFilters = NO_EXTRA_FILTERS,
  quickFilters,
  onToggleQuickFilter,
  extraChip,
  countText,
  viewOptions,
  view,
  onViewChange,
  viewAriaLabel,
  metaActions,
  advancedOpen,
  onToggleAdvanced,
  advancedActiveCount,
  advancedFields,
  sortOptions,
  sortKey,
  sortDir,
  onSortChange,
  savedViews,
  onResetAdvanced,
  onResetAll,
  compact,
  sheetResultLabel,
  onSheetOpenChange,
}: LibraryListToolbarProps<C, V>) {
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const filterToggleRef = React.useRef<HTMLButtonElement>(null);
  const sortId = React.useId();
  React.useEffect(() => {
    if (!compact) setSheetOpen(false);
  }, [compact]);
  React.useEffect(() => {
    onSheetOpenChange?.(sheetOpen && compact);
  }, [compact, onSheetOpenChange, sheetOpen]);

  const hasFacet = facetOptions.length > 0;
  const facetDefault = facetOptions[0]?.value ?? '';
  const facetActive = hasFacet && facet !== facetDefault;
  const activeQuickFilters = quickFilters.filter((filter) => filter.active);
  const sheetActiveCount = Number(siteFilterActive)
    + Number(facetActive)
    + Number(statusFilterActive)
    + extraFilters.filter((filter) => filter.active).length
    + activeQuickFilters.length
    + advancedActiveCount;

  const phoneChips: LibraryFilterChip[] = [
    ...(facetActive
      ? [{
        key: 'facet',
        label: `${facetLabel}: ${facetOptions.find((option) => option.value === facet)?.label ?? facet}`,
        onRemove: () => onFacetChange?.(facetDefault),
      }]
      : []),
    ...activeQuickFilters.map((filter) => ({
      key: `quick:${filter.key}`,
      label: filter.label,
      onRemove: () => onToggleQuickFilter(filter.key),
    })),
    ...(advancedActiveCount > 0
      ? [{
        key: 'advanced',
        label: advancedActiveCount === 1 ? '1 advanced filter' : `${advancedActiveCount} advanced filters`,
        onRemove: onResetAdvanced,
      }]
      : []),
    ...(extraChip ? [{ key: 'extra', label: extraChip.label, onRemove: extraChip.onClear }] : []),
  ];

  const handleSortChange = React.useCallback(function handleSortChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const [nextKey, nextDir] = event.target.value.split(':');
    onSortChange(nextKey, nextDir === 'desc' ? 'desc' : 'asc');
  }, [onSortChange]);

  const facetGroup = hasFacet ? (
    <div className="library-list-facet" role="group" aria-label={facetLabel}>
      {facetOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          className="library-list-facet__option"
          aria-pressed={facet === option.value}
          onClick={() => onFacetChange?.(option.value)}
        >
          {option.label}
          {option.count != null ? <span className="library-list-facet__count">{option.count.toLocaleString()}</span> : null}
        </button>
      ))}
    </div>
  ) : null;

  const quickFilterGroup = quickFilters.length > 0 ? (
    <div className="library-list-quick-filters" role="group" aria-label="Quick filters">
      {quickFilters.map((filter) => (
        <button
          key={filter.key}
          type="button"
          className="library-list-quick-filter"
          data-tone={filter.tone}
          aria-pressed={filter.active}
          onClick={() => onToggleQuickFilter(filter.key)}
        >
          {filter.label}
          {filter.count != null ? <span className="library-list-quick-filter__count">({filter.count.toLocaleString()})</span> : null}
        </button>
      ))}
    </div>
  ) : null;

  const sortField = (
    <label className="library-toolbar__field text-xxs fg-muted" htmlFor={sortId}>
      <span>Sort by</span>
      <select id={sortId} className="xs" value={`${sortKey}:${sortDir}`} onChange={handleSortChange}>
        {sortOptions.flatMap((option) => [
          option.ascLabel === null ? null : (
            <option key={`${option.value}:asc`} value={`${option.value}:asc`}>{option.ascLabel ?? `${option.label}, first to last`}</option>
          ),
          option.descLabel === null ? null : (
            <option key={`${option.value}:desc`} value={`${option.value}:desc`}>{option.descLabel ?? `${option.label}, last to first`}</option>
          ),
        ])}
      </select>
    </label>
  );

  const isPhone = useLibraryPhoneLayout();
  const shownViewOptions = isPhone && viewOptions ? viewOptions.filter((option) => option.value !== 'grid') : viewOptions;
  const shownView = isPhone && view === 'grid' ? shownViewOptions?.find((option) => option.value === 'list')?.value ?? view : view;
  const viewToggle = shownViewOptions && shownViewOptions.length > 1 && shownView != null && onViewChange ? (
    <LibraryViewToggle value={shownView} options={shownViewOptions} onChange={onViewChange} ariaLabel={viewAriaLabel} />
  ) : null;

  return (
    <>
      <div className="library-toolbar-block library-list-toolbar-block" data-open={advancedOpen && !compact} data-active={advancedActiveCount > 0}>
        <div className="card soft library-toolbar library-list-toolbar">
          <div className="library-toolbar__search">
            <LibrarySearchInput value={search} onChange={onSearchChange} placeholder={compact && searchPlaceholderShort ? searchPlaceholderShort : searchPlaceholder} label={searchLabel} />
            {compact ? (
              <LibraryFilterToggle
                ref={filterToggleRef}
                expanded={sheetOpen}
                activeCount={sheetActiveCount}
                aria-haspopup="dialog"
                onClick={() => setSheetOpen(true)}
              />
            ) : null}
          </div>
          {compact ? (
            <LibraryFilterChips chips={phoneChips} label="Active filters" emptyFocusRef={filterToggleRef} />
          ) : (
            <div className="library-toolbar__filters library-list-toolbar__filters">
              {siteControl != null ? (
                <div className="library-toolbar__field library-toolbar__field--site text-xxs fg-muted">
                  <span>Sites</span>
                  {siteControl}
                </div>
              ) : null}
              {facetGroup}
              {statusControl != null ? (
                <div className="library-toolbar__field library-toolbar__field--compact text-xxs fg-muted">
                  {statusControl}
                </div>
              ) : null}
              {extraFilters.map((filter) => (
                <div key={filter.key} className="library-toolbar__field text-xxs fg-muted">
                  <span>{filter.label}</span>
                  {filter.control}
                </div>
              ))}
              {quickFilterGroup}
            </div>
          )}
          <div className="library-toolbar__meta">
            <div className="lib-count" aria-live="polite">
              {countText}
              {!compact && extraChip ? (
                <button type="button" className="library-list-linked-chip" onClick={extraChip.onClear} aria-label={`${extraChip.label}: clear`}>
                  {extraChip.label} <span aria-hidden="true">{'×'}</span>
                </button>
              ) : null}
            </div>
            {viewToggle}
            {metaActions}
          </div>
          {!compact ? (
            <LibraryAdvancedToggle expanded={advancedOpen} active={advancedActiveCount > 0} onClick={onToggleAdvanced} />
          ) : null}
        </div>
        {!compact ? (
          <div className="library-toolbar__advanced" data-open={advancedOpen}>
            <div className="library-toolbar__advanced-grid">
              {advancedFields}
              {sortField}
            </div>
            <LibrarySavedViews controller={savedViews} />
            <div className="library-toolbar__advanced-actions">
              <button type="button" className="btn micro" onClick={onResetAdvanced} disabled={advancedActiveCount === 0}>
                Reset filters
              </button>
            </div>
          </div>
        ) : null}
      </div>
      <LibraryFilterSheet
        open={sheetOpen && compact}
        onClose={() => setSheetOpen(false)}
        onReset={onResetAll}
        resetDisabled={sheetActiveCount === 0}
        resultLabel={sheetResultLabel}
        returnFocusRef={filterToggleRef}
      >
        {siteControl != null ? <LibraryFilterSheetGroup label="Sites">{siteControl}</LibraryFilterSheetGroup> : null}
        {facetGroup ? <LibraryFilterSheetGroup label={facetLabel}>{facetGroup}</LibraryFilterSheetGroup> : null}
        {statusControl != null ? <LibraryFilterSheetGroup label="Status">{statusControl}</LibraryFilterSheetGroup> : null}
        {extraFilters.map((filter) => (
          <LibraryFilterSheetGroup key={filter.key} label={filter.label}>{filter.control}</LibraryFilterSheetGroup>
        ))}
        {quickFilterGroup ? <LibraryFilterSheetGroup label="Quick filters">{quickFilterGroup}</LibraryFilterSheetGroup> : null}
        <LibraryFilterSheetGroup label="More filters">
          <div className="library-list-sheet-advanced">
            {advancedFields}
            {sortField}
          </div>
        </LibraryFilterSheetGroup>
        <LibraryFilterSheetGroup label="Views">
          <LibrarySavedViews controller={savedViews} />
        </LibraryFilterSheetGroup>
      </LibraryFilterSheet>
    </>
  );
}
